import { css } from "@emotion/react";
import { vars } from "@imwebme/clay-token";
import type { ElementType } from "react";
import { match } from "ts-pattern";
import type { ButtonProps } from "./Button.types";

const {
  semantic: { color, typography },
  spacing,
  rounded,
} = vars;

const medium = css`
  padding: ${spacing[2]} ${spacing[3]};
  min-width: 64px;
  border-radius: ${rounded.medium};

  ${typography["label-medium-bold"]}
`;

const large = css`
  padding: ${vars.spacing[3]} ${vars.spacing[4]};
  min-width: 80px;
  border-radius: ${vars.rounded.medium};

  ${typography["label-large-bold"]}
`;

const small = css`
  padding: ${vars.spacing[1]} ${vars.spacing[3]};
  min-width: 48px;
  border-radius: ${vars.rounded.small};

  ${typography["label-medium-bold"]}
`;

const tiny = css`
  padding: ${vars.spacing[15]} ${vars.spacing[2]};
  min-width: 40px;
  border-radius: ${vars.rounded.small};

  ${typography["label-small-bold"]}
`;

export const sizeStyles = {
  medium,
  large,
  small,
  tiny,
};

const outlined = () =>
  css({
    "--clay-button-background-color": color.actionSecondary,

    backgroundColor: "var(--clay-button-background-color)",
    "--clay-dot-bg": "var(--clay-button-background-color)",

    color: color.textSecondary,
    svg: {
      color: color.iconSecondary,
    },

    outline: `1px solid ${color.border}`,
    outlineOffset: "-1px",

    "&:hover:not(:disabled)": {
      outlineColor: color.borderHover,
    },

    "&:active:not(:disabled)": {
      outlineColor: color.borderSub,
    },

    "&:disabled": {
      "--clay-button-background-color": color.actionSecondaryDisabled,

      color: color.textDisabled,
      outlineColor: "transparent",
      svg: {
        color: color.textDisabled,
      },

      '&[data-imweb-loading="true"]': {
        "--clay-button-background-color": color.actionSecondary,

        color: color.textSecondary,
        outlineColor: color.borderSub,
        svg: {
          color: color.icon,
        },
      },
    },
  });

const primary = <E extends ElementType>(args?: Extract<ButtonProps<E>, { variant: "primary" }>) =>
  css({
    "--clay-button-background-color": args?.tonal ? color.actionPrimaryTonal : color.actionPrimary,

    backgroundColor: "var(--clay-button-background-color)",
    "--clay-dot-bg": "var(--clay-button-background-color)",

    color: args?.tonal ? color.textSecondary : color.textInverse,
    svg: {
      color: args?.tonal ? color.iconSecondary : color.iconInverse,
    },

    "&:hover:not(:disabled)": {
      "--clay-button-background-color": args?.tonal
        ? color.actionPrimaryTonalHover
        : color.actionPrimaryHover,
    },

    "&:active:not(:disabled)": {
      "--clay-button-background-color": args?.tonal
        ? color.actionPrimaryTonalPressed
        : color.actionPrimaryPressed,
    },

    "&:disabled": {
      color: color.textDisabled,
      svg: {
        color: color.textDisabled,
      },

      '&:not([data-imweb-loading="true"])': {
        "--clay-button-background-color": args?.tonal
          ? color.actionPrimaryTonalDisabled
          : color.actionPrimaryDisabled,

        svg: {
          color: color.textDisabled,
        },
      },

      '&[data-imweb-loading="true"]': {
        "--clay-button-background-color": args?.tonal
          ? color.actionPrimaryTonalPressed
          : color.actionPrimaryPressed,

        color: args?.tonal ? color.textSecondary : color.textInverse,
        svg: {
          color: args?.tonal ? color.icon : color.iconInverse,
        },
      },
    },
  });

const secondary = () =>
  css({
    "--clay-button-background-color": "transparent",

    backgroundColor: "var(--clay-button-background-color)",
    "--clay-dot-bg": "var(--clay-button-background-color)",

    color: color.textSecondary,
    svg: {
      color: color.iconSecondary,
    },

    "&:hover:not(:disabled)": {
      "--clay-button-background-color": color.actionSecondaryHover,
    },

    "&:active:not(:disabled)": {
      "--clay-button-background-color": color.actionSecondaryPressed,
    },

    "&:disabled": {
      "--clay-button-background-color": color.actionSecondaryDisabled,

      color: color.textDisabled,
      svg: {
        color: color.textDisabled,
      },
      '&[data-imweb-loading="true"]': {
        "--clay-button-background-color": "transparent",

        color: color.textSecondary,
        svg: {
          color: color.icon,
        },
      },
    },
  });

const critical = <E extends ElementType>(args?: Extract<ButtonProps<E>, { variant: "critical" }>) =>
  css({
    "--clay-button-background-color": args?.tonal
      ? color.actionCriticalTonal
      : color.actionCritical,

    backgroundColor: "var(--clay-button-background-color)",
    "--clay-dot-bg": "var(--clay-button-background-color)",

    color: args?.tonal ? color.textCritical : color.textOn,
    svg: {
      color: args?.tonal ? color.iconCritical : color.iconOn,
    },

    "&:hover:not(:disabled)": {
      "--clay-button-background-color": args?.tonal
        ? color.actionCriticalTonalHover
        : color.actionCriticalHover,
    },

    "&:active:not(:disabled)": {
      "--clay-button-background-color": args?.tonal
        ? color.actionCriticalTonalPressed
        : color.actionCriticalPressed,
    },

    "&:disabled": {
      color: color.textDisabled,
      svg: {
        color: color.textDisabled,
      },

      '&:not([data-imweb-loading="true"])': {
        "--clay-button-background-color": args?.tonal
          ? color.actionCriticalTonalDisabled
          : color.actionCriticalDisabled,

        stroke: color.textDisabled,
      },

      '&[data-imweb-loading="true"]': {
        "--clay-button-background-color": args?.tonal
          ? color.actionCriticalTonalPressed
          : color.actionCriticalPressed,

        color: args?.tonal ? color.textCritical : color.textOn,
        svg: {
          color: args?.tonal ? color.iconCritical : color.iconOn,
        },
      },
    },
  });

const accent = <E extends ElementType>(args?: Extract<ButtonProps<E>, { variant: "accent" }>) =>
  css({
    "--clay-button-background-color": args?.tonal ? color.actionAccentTonal : color.actionAccent,

    backgroundColor: "var(--clay-button-background-color)",
    "--clay-dot-bg": "var(--clay-button-background-color)",

    color: args?.tonal ? color.textAccent : color.text,
    svg: {
      color: args?.tonal ? color.iconAccent : color.icon,
    },

    "&:hover:not(:disabled)": {
      "--clay-button-background-color": args?.tonal
        ? color.actionAccentTonalHover
        : color.actionAccentHover,
    },

    "&:active:not(:disabled)": {
      "--clay-button-background-color": args?.tonal
        ? color.actionAccentTonalPressed
        : color.actionAccentPressed,
    },

    "&:disabled": {
      color: color.textDisabled,
      svg: {
        color: color.textDisabled,
      },

      '&:not([data-imweb-loading="true"])': {
        "--clay-button-background-color": args?.tonal
          ? color.actionAccentTonalDisabled
          : color.actionAccentDisabled,

        stroke: color.textDisabled,
      },

      '&[data-imweb-loading="true"]': {
        "--clay-button-background-color": args?.tonal
          ? color.actionAccentTonalPressed
          : color.actionAccentPressed,

        color: args?.tonal ? color.textAccent : color.text,
        svg: {
          color: args?.tonal ? color.iconAccent : color.icon,
        },
      },
    },
  });

export const buttonCSS = <E extends ElementType>(props: ButtonProps<E>) => {
  const { fullWidth, rounded, size = "medium" } = props;
  const gapToken = size === "large" || size === "medium" ? 2 : 1;

  const variantStyle = match(props)
    .with({ variant: "primary" }, primary)
    .with({ variant: "secondary" }, secondary)
    .with({ variant: "critical" }, critical)
    .with({ variant: "accent" }, accent)
    .with({ variant: "outlined" }, outlined)
    .exhaustive();

  return css`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: ${spacing[gapToken]};
    ${sizeStyles[size]};

    ${variantStyle};
    ${rounded && `border-radius: ${vars.rounded.full};`}

    width: ${fullWidth ? "100%" : "fit-content"};
    height: fit-content;

    transition-timing-function: ease-out;
    transition-duration: 100ms;
    transition-property: background-color, color;
  `;
};
